import { ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type {
  DeleteWidgetMutationInput,
  Mutation,
  Query,
  SaveWidgetMutationInput,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import { CONNECTION_METADATA_FIELDS } from '../../connection/Api';
import type { ConnectionArguments } from '../../connection/Api';
import * as GqlConnection from '../../connection/GqlConnection';
import type * as Ordering from '../../Shared/Ordering';
import * as WidgetConfig from './WidgetConfig';
import * as WidgetConfigInput from './WidgetConfigInput';
import type { WidgetConfigSearchArguments } from './WidgetConfigSearchArguments';
import { WIDGET_CONFIG_FIELDS } from './fragments';

/**
 * Widget-configuration administration on the tenant (AppSchema) connection. The same three fields
 * exist on the legacy schema nested under `app { … }`, deprecated for removal in 6.1; the read
 * there took an `accountId`, where this one gets the account from the `X-VP-Account-Id` header.
 *
 * **Nothing here catches.** A transport failure throws and propagates; `VPResult`'s
 * `success: false` covers only the GraphQL-level outcomes — an absent payload, a populated
 * `errors` list. That matches `App/Venue` and preserves the legacy store's
 * `try`/`finally`-with-no-`catch`. This is a transport move, not a change of failure semantics.
 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

/**
 * The page size the backend declares on `widgetConfigurations` (`default_page_size: 100`); its
 * `max_page_size` is 250, so a larger request is clamped without a word.
 */
export const DEFAULT_PAGE_SIZE = 100;

export const WIDGET_CONFIGS_QUERY = gql`
  query WidgetConfigurations(
    $after: String, $before: String, $first: Int, $last: Int, $filter: WidgetConfigSearchArguments, $sort: [OrderingArgument!]
  ) {
    widgetConfigurations(after: $after, before: $before, first: $first, last: $last, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          ...WidgetConfigFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
      totalCount
    }
  }
  ${WIDGET_CONFIG_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

export const WIDGET_CONFIG_QUERY = gql`
  query WidgetConfiguration($id: Int!) {
    widgetConfiguration(id: $id) {
      ...WidgetConfigFields
    }
  }
  ${WIDGET_CONFIG_FIELDS}
`;

export const SAVE_WIDGET_MUTATION = gql`
  mutation saveWidget($input: SaveWidgetMutationInput!) {
    saveWidget(input: $input) {
      widget {
        ...WidgetConfigFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${WIDGET_CONFIG_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_WIDGET_MUTATION = gql`
  mutation deleteWidget($input: DeleteWidgetMutationInput!) {
    deleteWidget(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/**
 * `network-only` for the reason the legacy read used it: the widget screens refetch after a save
 * or a delete, and a cached list would show the state they just changed.
 */
export const fetchWidgetConfigs = async (
  client: ApolloClient<NormalizedCacheObject>,
  pagination?: ConnectionArguments,
  filter?: WidgetConfigSearchArguments,
  sort?: Ordering.Ordering<'created_at'>[],
): Promise<GqlConnection.GqlConnection<WidgetConfig.WidgetConfig>> => {
  const paged = pagination ?? {};

  // Assigned rather than spread in conditionally so `first` stays an *absent* key when it does
  // not apply, not present-and-undefined.
  const variables: Record<string, unknown> = { ...paged };

  // Only on an unsized forward page. Adding `first` to a `before` page would read the first
  // N of the whole set rather than the page before the cursor, and pairing it with `last`
  // would slice twice.
  if (paged.first === undefined && paged.last === undefined && paged.before === undefined) {
    variables.first = DEFAULT_PAGE_SIZE;
  }

  if (filter) {
    variables.filter = filter;
  }

  if (sort) {
    variables.sort = sort;
  }

  const { data } = await client.query<Query>({
    query: WIDGET_CONFIGS_QUERY,
    fetchPolicy: 'network-only',
    variables,
  });

  return WidgetConfig.fromGqlConnection(data.widgetConfigurations);
};

/** The field answers an unknown id and one outside the account's scope identically, with null. */
export const fetchWidgetConfig = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<WidgetConfig.WidgetConfig | undefined> => {
  const { data } = await client.query<Query>({
    query: WIDGET_CONFIG_QUERY,
    fetchPolicy: 'network-only',
    variables: { id },
  });

  return data.widgetConfiguration ? WidgetConfig.fromGql(data.widgetConfiguration) : undefined;
};

/** Creates when the input carries no `id`, updates when it does. */
export const saveWidgetConfig = async (
  client: ApolloClient<NormalizedCacheObject>,
  widget: WidgetConfigInput.WidgetConfigInput,
): Promise<VPResult<WidgetConfig.WidgetConfig, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: SaveWidgetMutationInput = { widget: WidgetConfigInput.toGqlInput(widget) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_WIDGET_MUTATION,
    variables: { input },
  });

  const payload = data?.saveWidget;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // This payload carries no `success`, and AppSchema returns an empty `errors` array where the
  // legacy one omitted the key — so an empty error list with no entity is the one shape that
  // would otherwise read as a silent success.
  if (!payload?.widget) {
    return failure('Failed to save the widget configuration.');
  }

  return { success: true, data: WidgetConfig.fromGql(payload.widget) };
};

export const deleteWidgetConfig = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  // `Int!`, not the `ID!` `deleteVenue` takes — the caller's number goes over as it is.
  const input: DeleteWidgetMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_WIDGET_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteWidget;

  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  // A failure the caller cannot render is worse than a generic message.
  return errors.length ? { success: false, errors } : failure('Failed to delete the widget configuration.');
};
